import type {
  CreateSignoffInput,
  ProcessOrigin,
  Signoff,
  SignoffEvidence,
  UnassignedInScope,
} from '@testflow/shared';
import { request } from './api.js';

// GET /signoffs liefert je Struktur-Knoten (Teilprojekt/Stream) entweder den aktiven Sign-off
// (Felder befüllt) oder "offen" (Felder null) — node_id/node_name sind immer gesetzt.
// #83 (Task 6): process_origin beantwortet "woraus speist sich das?" (Kette Testfall → Prozess →
// process_labels → Knoten).
// Whole-Branch-Review (Umbau von #83): der Server liefert wieder ALLE Knoten, ungefiltert — es gibt
// keinen separaten empty-nodes-Endpoint mehr. Ob/wie ein Knoten dargestellt wird, entscheiden die
// Regeln unten (isConfirmed/hasEvidenceInScope/isOrphaned/isEvidenceDrifted/needsSignoffNote), damit Sign-off-Seite,
// Abnahmestand im Testplan-Detail und Dashboard-Karte dieselbe Fachlogik benutzen statt sie je
// Konsument neu zu erfinden.
export type SignoffListItem = Pick<Signoff, 'node_id' | 'node_name'> & {
  signoff_id: string | null;
  confirmer_user_id: string | null;
  confirmer_name: string | null;
  confirmed_at: string | null;
  note: string | null;
  evidence: SignoffEvidence | null;
  revoked_at: string | null;
  // #88: true nur bei aktivem Sign-off, dessen Grundlage sich seit der Freigabe geändert hat
  // (Umfang oder Ergebnisse) — der Server rechnet das, der Client zeigt es.
  evidence_drift: boolean;
  // #88: nur an Knoten OHNE aktiven Sign-off — wer wann widerrufen hat und warum (reason ist bei
  // Altbestand vor der Begründungspflicht null).
  last_revocation: {
    revoked_at: string;
    revoked_by_name: string | null;
    reason: string | null;
  } | null;
  process_origin: ProcessOrigin;
};

// #88 (Final-Review F3): Antwort des Widerrufs — nur die Widerrufsdaten, keine volle Kartenzeile.
export type RevokedSignoff = {
  signoff_id: string;
  node_id: string;
  revoked_at: string;
  revoked_by: string;
  revoke_reason: string;
};

/**
 * Was „abgenommen" fachlich bedeutet — hier, weil sowohl die Sign-off-Seite als auch der
 * Abnahmestand im Testplan-Detail die Regel brauchen. Zweimal ausgeschrieben würde sie
 * auseinanderdriften, sobald jemand den Widerruf anders behandelt.
 */
export function isConfirmed(item: SignoffListItem): boolean {
  return item.signoff_id !== null && item.revoked_at === null;
}

/** Hat der Knoten mindestens einen Testfall im Scope DIESES Plans? */
export function hasEvidenceInScope(item: SignoffListItem): boolean {
  return (item.evidence?.total_in_scope ?? 0) > 0;
}

// Whole-Branch-Review (Umbau von #83): ein Knoten OHNE Testfall im Scope UND OHNE aktiven Sign-off
// ist nicht aktionabel — es gibt nichts abzunehmen, eine Karte dafür wäre nur Rauschen (Konsens aus
// #83). Ein Knoten MIT aktivem Sign-off gehört dagegen IMMER in die sichtbare Liste, selbst wenn
// sein einziger Testfall inzwischen aus dem Plan-Scope genommen wurde (siehe isOrphaned) — sonst
// verschwindet eine erteilte Abnahme spurlos und der Widerrufen-Knopf wird unerreichbar.
export function isVisibleSignoffCard(item: SignoffListItem): boolean {
  return hasEvidenceInScope(item) || isConfirmed(item);
}

// Blocker (Whole-Branch-Review): ein bereits bestätigter Sign-off, dessen Grundlage (der einzige
// Testfall im Scope) danach aus dem Plan genommen wurde — die Bestätigung selbst bleibt aktiv
// (revoked_at === null), aber sie beruht auf nichts mehr. Muss hervorgehoben werden statt versteckt.
export function isOrphaned(item: SignoffListItem): boolean {
  return isConfirmed(item) && !hasEvidenceInScope(item);
}

// #88: verallgemeinert isOrphaned — die Grundlage einer aktiven Abnahme hat sich geändert (Testfälle
// kamen hinzu, bestandene Läufe fielen weg). Die Abnahme bleibt stehen und sichtbar, wird aber
// hervorgehoben; verdeckt oder automatisch widerrufen wird sie nie, die Entscheidung bleibt beim
// Menschen. Das Flag rechnet der Server (er kennt den Stand bei der Freigabe), nicht der Client.
export function isEvidenceDrifted(item: SignoffListItem): boolean {
  return isConfirmed(item) && item.evidence_drift;
}

// #88: Kundenentscheidung 13.10. — der Kommentar ist Pflicht, sobald nicht ALLE Testfälle im Scope
// bestanden sind. Ohne Testfälle im Scope (0/0) gibt es nichts, was nicht bestanden wäre. Der Server
// prüft dieselbe Regel (signoff_note_required); hier nur, damit der Dialog sie vorab erzwingt.
export function needsSignoffNote(item: SignoffListItem): boolean {
  return (item.evidence?.passed_run_count ?? 0) < (item.evidence?.total_in_scope ?? 0);
}

// „Steht noch aus" heißt: nicht bestätigt UND es gibt überhaupt etwas abzunehmen. Ein Knoten ohne
// Testfall im Scope ist keine offene Abnahme, sondern gar keine. Die Regel steht hier und nicht
// zweimal ausgeschrieben im Dashboard — sonst driften die beiden Zähler auseinander, und genau
// diese Drift-Klasse war der Anlass, die Sichtbarkeitsregeln überhaupt hierher zu ziehen.
export function isPendingSignoff(item: SignoffListItem): boolean {
  return !isConfirmed(item) && hasEvidenceInScope(item);
}

const base = (p: string) => `/v1/projects/${p}`;

export const signoffApi = {
  // #55: test_plan_id ist jetzt Pflichtparameter — die Route rät den Plan nicht mehr implizit.
  list: (p: string, planId: string): Promise<SignoffListItem[]> =>
    request<SignoffListItem[]>(`${base(p)}/signoffs?test_plan_id=${encodeURIComponent(planId)}`),
  confirm: (p: string, body: CreateSignoffInput): Promise<SignoffListItem> =>
    request<SignoffListItem>(`${base(p)}/signoffs`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // #88: der Widerruf verlangt eine Begründung (Body `{ reason }`, serverseitig getrimmt 1..2000).
  revoke: (p: string, signoffId: string, reason: string): Promise<RevokedSignoff> =>
    request<RevokedSignoff>(`${base(p)}/signoffs/${signoffId}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  // #34: In-Scope-Testfälle ohne Sign-off-Knoten-Zuordnung (projektweite Zusatzsicht).
  unassigned: (p: string, planId: string): Promise<UnassignedInScope> =>
    request<UnassignedInScope>(
      `${base(p)}/signoffs/unassigned?test_plan_id=${encodeURIComponent(planId)}`,
    ),
};
